import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
} from 'react';
import type { ChangeEvent, PropsWithoutRef } from 'react';
import _debounce from 'lodash/debounce';

import Input, { inputClasses } from '../Input';
import type { InputProps } from '../Input';

export type DebounceInputProps = {
  timeout?: number;
} & InputProps;

const DebounceInput = forwardRef<
  HTMLInputElement,
  PropsWithoutRef<DebounceInputProps>
>(({ timeout = 400, value, onChange, ...rest }, ref) => {
  const skip = useRef<boolean>(false);

  const [inputValue, setInputValue] = useState<string>(String(value) ?? '');

  const debouncedHandler = useMemo(
    () =>
      _debounce((event: ChangeEvent<HTMLInputElement>) => {
        if (!skip.current && onChange) {
          onChange(event);
        }
      }, timeout),
    [timeout, onChange]
  );

  const handleChangeInputValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      skip.current = false;
      setInputValue(event.target.value);
      debouncedHandler(event);
    },
    [debouncedHandler]
  );

  useEffect(() => {
    skip.current = true;
    setInputValue(String(value) ?? '');
  }, [value]);

  return (
    <Input
      ref={ref}
      value={inputValue}
      onChange={handleChangeInputValue}
      {...rest}
    />
  );
});

export { inputClasses };
export default DebounceInput;
